// 媒体权限预检（麦克风/摄像头）——SDK Bridge Protocol v1 §4
// queryPermission/requestPermission/openAppSettings + permissionResult。
// 权威契约见 docs/SDK_BRIDGE_PROTOCOL.md，native 三端由另一组 agent 实现。
//
// 用途：通话发起前（未来还有语音消息录制按钮）先问一次系统权限，blocked 就
// 别再调 getUserMedia——在原生 WebView 容器里，浏览器自己弹的网页权限提示
// 大概率是死的（有些 WebView 配置下 getUserMedia 在权限被拒后连一次系统弹窗
// 都不会有，静默失败），访客只会看到"通话连不上"却不知道为什么。改成主动
// 查一次系统权限，被拒就直接展示"去设置"引导条，不再尝试 getUserMedia。
//
// 无 bridge / bridge 不支持 queryPermission 时，本模块所有函数都直接放行
// （'proceed'），调用方原有 getUserMedia/RealtimeKit 流程完全不变——网页端
// 零变化，这是本模块最重要的不变量，任何改动都不能破坏它。
//
// 纯函数（normalizePermissionCallResult/reducePermissionChannels/
// shouldShowGuidanceBar/shouldShowSettingsButton）不摸 bridge/Promise/
// 事件监听，node --test 直接灌数据断言；async 编排函数
// （ensureMediaPermission）是这些纯函数 + native-bridge.js 的胶水，不单测
// 编排本身（那是 native-bridge.js call()/onNativeEvent() 已经测过的行为
// 组合），只测决策逻辑。

import bridge from './native-bridge.js';

// ---------- 纯函数 ----------

/**
 * bridge.call('queryPermission'|'requestPermission', {kind}) 的原始回执
 * 归一化成三态之一，或者 null（表示"这条通道不可信，调用方应该整体回退到
 * web 权限流"）。
 *   - result.unsupported → null（native 不认识这个 call；理论上不会发生，
 *     调用前已经用 bridge.supports() 判过，防御一下）
 *   - result.timeout / !result.ok / state 不是三态之一 → 'prompt'
 *     （bridge call 3s 超时是协议明确约定的"按 prompt 处理"情形——不是
 *     "问不到"，是"还没问完"，真正的终态等 permissionResult 事件补发）
 *   - 合法 state → 原样返回
 */
export function normalizePermissionCallResult(result) {
    if (!result) return 'prompt';
    if (result.unsupported) return null;
    if (result.timeout) return 'prompt';
    if (!result.ok) return 'prompt';
    if (result.state === 'granted' || result.state === 'blocked' || result.state === 'prompt') {
        return result.state;
    }
    return 'prompt';
}

/**
 * 双通道（requestPermission 的 call 回执 / permissionResult 事件）去重
 * reducer——谁先到出终态(granted/blocked)就定局，定局之后再来的 action
 * 一律忽略；'prompt' 不是终态，只会让状态保持"还在等"。纯函数，不知道
 * Promise/事件监听这些编排细节，只表达"给一串到达顺序不同的信号，该收敛
 * 成什么"这条规则本身——两个通道谁先到、要不要都到、到达间隔多久，全部
 * 是这个函数的外部输入，测试直接构造 action 序列断言即可，不需要假计时器。
 *
 * @param {{settled: boolean, result: string|null}} state
 * @param {{source: 'call'|'event', state: 'granted'|'prompt'|'blocked'}} action
 * @returns {{settled: boolean, result: string|null}}
 */
export function reducePermissionChannels(state, action) {
    if (state.settled) return state;
    if (action.state === 'granted' || action.state === 'blocked') {
        return { settled: true, result: action.state };
    }
    return state;
}

export function initialPermissionChannelState() {
    return { settled: false, result: null };
}

/** blocked 才展示引导条——prompt 是"系统框还没问完/用户还没选"的中间态，
 * 不该在这时候打断；granted 直接走原有流程，用不上引导条。 */
export function shouldShowGuidanceBar(state) {
    return state === 'blocked';
}

/** 引导条"去设置"按钮：没有 openAppSettings 能力就不放一个点了没反应的
 * 按钮——引导条退化成纯文案。 */
export function shouldShowSettingsButton(supportsOpenAppSettings) {
    return !!supportsOpenAppSettings;
}

// ---------- 编排（native-bridge.js 的胶水，不单测这层本身） ----------

/**
 * 通话发起 / 未来的语音消息录制按钮按下时调用。只有两种返回值：
 *   'proceed' — 可以直接走原有 getUserMedia/RealtimeKit 流程（网页兜底、
 *               bridge 确认 granted、或者拿不到明确答案时的保守放行——
 *               "不确定"不该阻塞功能，交给 getUserMedia 自己的错误路径
 *               兜底，跟改动前行为一致）。
 *   'blocked' — bridge 明确说被拒，调用方应该展示引导条，不要再调
 *               getUserMedia（原生 WebView 里那个网页权限提示大概率是
 *               死的，见文件顶部注释）。
 * @param {'microphone'|'camera'} kind
 */
export async function ensureMediaPermission(kind) {
    if (!bridge.isActive() || !bridge.supports('queryPermission')) {
        return 'proceed'; // 无 bridge / 不支持——web 权限流，零变化
    }
    let queryState;
    try {
        queryState = normalizePermissionCallResult(await bridge.call('queryPermission', { kind }));
    } catch (e) {
        return 'proceed'; // bridge 调用本身炸了——保守放行，不要因为预检本身的异常挡住通话
    }
    if (queryState === null || queryState === 'granted') return 'proceed';
    if (queryState === 'blocked') return 'blocked';
    // queryState === 'prompt'：系统还没问过，正式发起一次请求。
    if (!bridge.supports('requestPermission')) return 'proceed';
    const finalState = await requestPermissionAwaitingTerminal(kind);
    return finalState === 'blocked' ? 'blocked' : 'proceed';
}

/**
 * requestPermission 的 call 回执和 permissionResult 事件，谁先带来终态用谁
 * （reducePermissionChannels），已经定局后另一路到达直接被 reducer 吞掉。
 * 两路都只给出 'prompt'（call 超时、或者理论上不会发生的 prompt 事件）时，
 * 这个 Promise 会挂起直到 permissionResult 真正带来终态——调用方（
 * ensureMediaPermission）没有再加一层整体超时：bridge.call 自己已经有
 * 3s 超时且明确按协议归入"等事件补发"，无限期挂起只会发生在 native 那边
 * 真的从未补发终态这种协议违约场景，不是本模块要兜底的范围。
 */
function requestPermissionAwaitingTerminal(kind) {
    return new Promise((resolve) => {
        let state = initialPermissionChannelState();
        let unsubscribe = () => {};

        const apply = (action) => {
            if (state.settled) return;
            state = reducePermissionChannels(state, action);
            if (state.settled) {
                unsubscribe();
                resolve(state.result);
            }
        };

        unsubscribe = bridge.onNativeEvent('permissionResult', (payload) => {
            if (!payload || payload.kind !== kind) return;
            apply({ source: 'event', state: normalizePermissionCallResult({ ok: true, state: payload.state }) });
        });

        bridge.call('requestPermission', { kind }).then((result) => {
            apply({ source: 'call', state: normalizePermissionCallResult(result) });
        });
    });
}

export default { ensureMediaPermission };
